import { LockOutlined } from "@ant-design/icons";
import { Button, Form, Input, Typography, message } from "antd";
import { useMutation } from "@tanstack/react-query";
import { useHistory, useLocation } from "react-router-dom";
import AppConfig from "utils/Config";
import { httpClient } from "utils/Api";

const ResetPassword = () => {
  const { Title } = Typography;
  const [form] = Form.useForm();
  const location = useLocation();
  const history = useHistory();
  
  // Get token from query parameter 'jeton'
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('jeton');

  const resetPasswordMutation = useMutation({
    mutationFn: (data) =>
      httpClient.post("/forgot-password/reset/", {
        token: token,
        password: data.password,
        password_confirm: data.password_confirm,
      }),
    onSuccess: () => {
      message.success("Password reset successfully! Redirecting to login...");
      form.resetFields();
      setTimeout(() => {
        history.push("/login");
      }, 1500);
    },
    onError: (error) => {
      let errorMsg = "Failed to reset password. Please try again.";
      if (error?.response?.data?.token) {
        errorMsg = "Reset link has expired. Please request a new one.";
      } else if (error?.response?.data?.password?.[0]) {
        errorMsg = error?.response?.data?.password?.[0];
      } else if (error?.response?.data?.detail) {
        errorMsg = error?.response?.data?.detail;
      }
      message.error(errorMsg);
    },
  });

  const onFinish = async (values) => {
    resetPasswordMutation.mutate(values);
  };

  return (
        <div className="min-h-screen bg-linear-to-b from-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-4">
          <img src={AppConfig.logo} alt="logo" width={100} />
        </div>

        <div className="bg-white rounded-lg shadow-lg p-5 mb-2">
          <Title level={4} className="text-center mb-2">
            Reset Your Password
          </Title>
          <p className="text-gray-600 text-center mb-6 text-sm">
            Enter your new password below.
          </p>

          {!token && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg my-4">
              Invalid reset link. Please request a new password reset.
            </div>
          )}

          {token && (
            <Form
              form={form}
              name="reset-password"
              onFinish={onFinish}
              className="space-y-6!"
              layout="vertical"
            >
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Please input your new password!",
                  },
                  {
                    min: 8,
                    message: "Password must be at least 8 characters!",
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="mr-2" />}
                  placeholder="New Password"
                  className="h-10"
                />
              </Form.Item>

              <Form.Item
                name="password_confirm"
                dependencies={["password"]}
                rules={[
                  {
                    required: true,
                    message: "Please confirm your password!",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match!")
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="mr-2" />}
                  placeholder="Confirm Password"
                  className="h-10"
                />
              </Form.Item>

              <Button
                type="primary"
                block
                htmlType="submit"
                loading={resetPasswordMutation.isPending}
                className="h-10 mt-6"
              >
                Reset Password
              </Button>
            </Form>
          )}

          <p className="text-center mb-0 text-sm text-gray-600">
            Back to{" "}
            <a href="/login" className="text-blue-600 font-semibold hover:text-blue-800">
              Sign In
            </a>
          </p>
        </div>

        <p className="text-center text-xs">
          {AppConfig.tagLine}
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
